// @givmo/board — starting a year's annual disclosures (CBG-YR2-WEB1; frames o2-start,
// o2b-start-questions).
//
// ══ IT SHOWS EVERYTHING THAT WILL HAPPEN BEFORE IT HAPPENS ══════════════════════════════════════
//
// Who gets the email, what it says, which questions, which policy: all on the dialog, and nothing is
// sent until "Start and send". The questions are Givmo's starting questionnaire as the backend serves
// it — its words, its sources ("From: …", on the officer's side only), its review line and provenance,
// shown verbatim — each with a switch to leave it out this year (Q2). The policy shown is the board's
// own, the version in force in Governance; with none, one factual line says the questions ask about
// interests only, and nothing says a policy is needed. The recipients are everyone on the roster now,
// directors or not (D-CBG-21). Nothing here says when a board "should" run this (§3.2, §3.6).

import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import {
  isGivmoError,
  type BoardCoiCycleSummaryOut,
  type OrgBoardMemberOut,
} from '@givmo/api-client';
import {
  Button,
  ErrorMessage,
  Icon,
  Select,
  Skeleton,
  Switch,
  TextField,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { CounselSentence } from '../../components/registered-disclaimer';
import { displayDate } from '../../lib/display';
import { useBoardOperations } from '../../board-runtime';
import { extractGoverningDocuments, versionInForce } from '../govdocs/govdocs-decode';
import {
  extractBoardMembers,
  isCommitteeOnlySeat,
  seatConnection,
  sortSeats,
} from '../roster/roster-decode';
import {
  KIND_ACKNOWLEDGMENT,
  PART_POLICY,
  askedOfDirectorsOnly,
  fillQuestion,
  nextLabel,
  periodLabel,
  todayIso,
} from './coi-decode';
import { emailLines } from './coi-officer-dialogs';
import { DialogFrame, EmailPreview, PartLabel, SourceLine, StatementText } from './coi-parts';
import { useCoiStartingQuestionnaire, useRefreshCoi } from './coi-queries';
import { coiWords as w } from './coi-words';

const SECONDARY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const LINK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none hover:text-brand-700 focus-visible:underline';

/** A calendar date a number of days after another. */
function daysAfter(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days);
  return todayIso(date.getTime());
}

/** The board's policy in force in Governance, as a year would freeze it: the latest standing one. */
export function usePolicyInForce(orgRef: string, enabled: boolean) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: ['board-coi-policy', orgRef],
    enabled,
    queryFn: async () => {
      const { data } = await operations.officerListBoardGoverningDocuments({
        path: { org_ref: orgRef },
        throwOnError: true,
      });
      const versions = extractGoverningDocuments(data)
        .filter((document) => document.document_kind === 'coi_policy')
        .map(versionInForce)
        .filter((version) => version !== null);
      const day = (version: (typeof versions)[number]) =>
        version.adopted_on ?? version.record.occurred_on ?? '';
      const latest = [...versions].sort((a, b) => (day(a) < day(b) ? 1 : -1))[0];
      return latest === undefined
        ? null
        : {
            title: latest.record.title ?? '',
            adoptedOn: latest.adopted_on ?? latest.record.occurred_on ?? null,
          };
    },
  });
}

export function StartDialog({
  orgRef,
  cycles,
  organization,
  onClose,
}: {
  orgRef: string;
  cycles: readonly BoardCoiCycleSummaryOut[];
  organization: string;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshCoi(orgRef);
  const questionnaireQuery = useCoiStartingQuestionnaire(orgRef, true);
  const policyQuery = usePolicyInForce(orgRef, true);
  const rosterQuery = useQuery({
    ...operations.officerListBoardMembersOptions({ path: { org_ref: orgRef } }),
    select: (body: unknown) => sortSeats(extractBoardMembers(body)),
  });

  const [label, setLabel] = useState(() => nextLabel(cycles));
  const [dueOn, setDueOn] = useState(() => daysAfter(todayIso(), 28));
  const [periodChoice, setPeriodChoice] = useState<'year' | 'dates'>('year');
  const year = Number(label) - 1;
  const yearStart = Number.isFinite(year) && year > 0 ? `${year}-01-01` : '';
  const yearEnd = Number.isFinite(year) && year > 0 ? `${year}-12-31` : '';
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const start = periodChoice === 'year' ? yearStart : periodStart;
  const end = periodChoice === 'year' ? yearEnd : periodEnd;
  const period = start && end ? periodLabel(start, end) : '';
  const [off, setOff] = useState<ReadonlySet<string>>(new Set());
  const [showQuestions, setShowQuestions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const questionnaire = questionnaireQuery.data ?? null;
  const policy = policyQuery.data ?? null;
  // The board's seats: someone who isn't on the board is never asked (CBG-YR6-OUTSIDE, edge 11).
  const people = useMemo<OrgBoardMemberOut[]>(
    () =>
      (rosterQuery.data ?? []).filter(
        (seat) => seat.status === 'active' && !isCommitteeOnlySeat(seat),
      ),
    [rosterQuery.data],
  );
  const notJoined = people
    .filter((seat) => seatConnection(seat) !== 'joined' && (seat.email_of_record ?? '').length > 0)
    .map((seat) => seat.display_name);
  const noEmail = people
    .filter((seat) => (seat.email_of_record ?? '').length === 0)
    .map((seat) => seat.display_name);
  const mailed = people.filter((seat) => (seat.email_of_record ?? '').length > 0);

  const questions = questionnaire?.questions ?? [];
  const statementShown = policy !== null;
  const shown = questions.filter((question) => question.part !== PART_POLICY || statementShown);
  // "6 questions about interests" (o2-start): every question but the policy's statement.
  const interestCount = questions.filter(
    (question) => question.part !== PART_POLICY && question.kind !== KIND_ACKNOWLEDGMENT,
  ).length;
  const interestsOn = questions.filter(
    (question) => question.part === 'interests' && !off.has(question.id),
  ).length;
  // The email as most people get it: someone who has joined Board, else the first person mailed.
  const firstPerson = mailed.find((seat) => seatConnection(seat) === 'joined') ?? mailed[0] ?? null;

  const open = useMutation({
    mutationFn: async () => {
      if (questionnaire === null) throw new Error(w.couldNotSave);
      const { data } = await operations.officerOpenBoardCoiCycle({
        path: { org_ref: orgRef },
        body: {
          label: label.trim(),
          period_start: start,
          period_end: end,
          due_on: dueOn,
          questionnaire_template_id: questionnaire.template_id,
          questionnaire_version: questionnaire.version,
          questions_off: [...off],
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: async (data) => {
      toast.success(
        w.started(label.trim(), typeof data?.emails_sent === 'number' ? data.emails_sent : 0),
      );
      await refresh();
      onClose();
    },
    onError: (caught) =>
      setError(isGivmoError(caught) && caught.message.trim() ? caught.message : w.couldNotSave),
  });

  const ready =
    questionnaire !== null &&
    label.trim().length > 0 &&
    dueOn.length > 0 &&
    start.length > 0 &&
    end.length > 0 &&
    start <= end &&
    interestsOn > 0 &&
    people.length > 0;

  const toggle = (id: string, asked: boolean) =>
    setOff((current) => {
      const next = new Set(current);
      if (asked) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <DialogFrame
      title={w.startYear(label.trim() || '…')}
      lead={w.startLead}
      testId="coi-start-dialog"
      width={600}
      onClose={onClose}
      footer={
        <>
          <Button appearance="saas" tone="secondary" onClick={onClose} testId="coi-start-cancel">
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            leadingIcon={<Icon icon={Send} size={16} aria-hidden />}
            disabled={!ready}
            loading={open.isPending}
            onClick={() => {
              setError(null);
              open.mutate();
            }}
            testId="coi-start-send"
          >
            {w.startAndSend(people.length)}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-12 sm:grid-cols-2">
        <TextField
          label={w.yearField}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          maxLength={40}
          testId="coi-start-year"
        />
        <TextField
          label={w.dueField}
          type="date"
          value={dueOn}
          onChange={(event) => setDueOn(event.target.value)}
          testId="coi-start-due"
        />
      </div>
      <Select
        label={w.periodField}
        appearance="saas"
        value={periodChoice}
        onValueChange={(next) => setPeriodChoice(next === 'dates' ? 'dates' : 'year')}
        options={[
          ...(year > 0 ? [{ value: 'year', label: w.periodYear(year) }] : []),
          { value: 'dates', label: w.periodOther },
        ]}
        testId="coi-start-period"
      />
      {periodChoice === 'dates' ? (
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2">
          <TextField
            label={w.periodStartField}
            type="date"
            value={periodStart}
            onChange={(event) => setPeriodStart(event.target.value)}
            testId="coi-start-period-start"
          />
          <TextField
            label={w.periodEndField}
            type="date"
            value={periodEnd}
            onChange={(event) => setPeriodEnd(event.target.value)}
            testId="coi-start-period-end"
          />
        </div>
      ) : null}

      <section className="flex flex-col gap-8" data-testid="coi-start-questions">
        <PartLabel>{w.questionsLabel}</PartLabel>
        {questionnaireQuery.isPending ? (
          <Skeleton className="h-84 w-full rounded-md" />
        ) : questionnaire === null ? (
          <ErrorMessage tone="muted">{w.questionsUnavailable}</ErrorMessage>
        ) : (
          <div className="flex flex-col gap-8 rounded-md border border-border-subtle p-12">
            <span className="flex flex-col gap-2">
              <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                {w.starting}
              </span>
              {/* The template's label, as small as it can be: the class's own review line and the
                  provenance, both backend strings shown verbatim; each question keeps its source. */}
              <span
                className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                data-testid="coi-template-line"
              >
                {questionnaire.review_line} {questionnaire.provenance}
              </span>
            </span>
            {/* D-CBG-62: Board's counsel sentence on its own line under the template's line. */}
            <CounselSentence orgRef={orgRef} testId="coi-template-counsel" />
            <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {w.questionsSummary(interestCount, statementShown)}
            </span>
            <button
              type="button"
              className={`${LINK} self-start`}
              onClick={() => setShowQuestions((value) => !value)}
              aria-expanded={showQuestions}
              data-testid="coi-start-questions-toggle"
            >
              {showQuestions ? w.hideQuestions : w.seeQuestions}
            </button>
            {showQuestions ? (
              <ol
                className="flex flex-col gap-12 border-t border-border-subtle pt-12"
                data-testid="coi-question-list"
              >
                {shown.map((question, index) => (
                  <li
                    key={question.id}
                    className="flex items-start gap-12"
                    data-testid={`coi-q-${question.id}`}
                  >
                    <span className="w-20 shrink-0 font-sans text-s13 font-w600 tabular-nums text-text-tertiary">
                      {index + 1}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-4">
                      <StatementText
                        lead={fillQuestion(question.text, organization, period || '…')}
                        points={(question.points ?? []).map((point) =>
                          fillQuestion(point, organization, period || '…'),
                        )}
                        className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong"
                      />
                      <span className="flex flex-wrap gap-x-10 gap-y-2">
                        <SourceLine source={question.source} />
                        {askedOfDirectorsOnly(question) ? (
                          <span className="font-sans text-s12 font-w600 tracking-givmo text-text-tertiary">
                            {w.directorsOnly}
                          </span>
                        ) : null}
                      </span>
                    </span>
                    <Switch
                      appearance="saas"
                      checked={!off.has(question.id)}
                      onCheckedChange={(asked) => toggle(question.id, asked)}
                      aria-label={w.askQuestion(index + 1)}
                      className="mt-2 shrink-0"
                      testId={`coi-q-switch-${question.id}`}
                    />
                  </li>
                ))}
              </ol>
            ) : null}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-8" data-testid="coi-start-policy">
        <PartLabel>{w.policyLabel}</PartLabel>
        {policyQuery.isPending ? (
          <Skeleton className="h-48 w-full rounded-md" />
        ) : policy === null ? (
          <p className={SECONDARY} data-testid="coi-start-no-policy">
            {w.noPolicy}
          </p>
        ) : (
          <div className="flex flex-col gap-8 rounded-md border border-border-subtle p-12">
            <span className="flex flex-col gap-2">
              <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                {policy.title}
              </span>
              <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
                {w.policyAdoptedFromGovernance(
                  policy.adoptedOn ? displayDate(policy.adoptedOn) : null,
                )}
              </span>
            </span>
            <FieldNote>{w.policyNote}</FieldNote>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-8" data-testid="coi-start-people">
        <PartLabel>{w.whosAsked}</PartLabel>
        {rosterQuery.isPending ? (
          <Skeleton className="h-48 w-full rounded-md" />
        ) : people.length === 0 ? (
          <p className={SECONDARY}>{w.nobodyOnRoster}</p>
        ) : (
          <>
            <p className={SECONDARY}>
              {w.everyoneOnRoster(people.map((seat) => seat.display_name))}
            </p>
            {notJoined.length > 0 ? <FieldNote>{w.notJoined(notJoined)}</FieldNote> : null}
            {noEmail.length > 0 ? <FieldNote>{w.noEmail(noEmail)}</FieldNote> : null}
          </>
        )}
      </section>

      {firstPerson !== null && dueOn.length > 0 ? (
        <section className="flex flex-col gap-8" data-testid="coi-start-email">
          <PartLabel>{w.theEmail}</PartLabel>
          <EmailPreview
            testId="coi-start-email-preview"
            organization={organization}
            subject={w.emailSubject(label.trim())}
            lines={emailLines('request', {
              name: firstPerson.display_name,
              organization,
              dueOn,
              joined: seatConnection(firstPerson) === 'joined',
            })}
          />
        </section>
      ) : null}

      {error !== null ? <ErrorMessage testId="coi-start-error">{error}</ErrorMessage> : null}
    </DialogFrame>
  );
}
